<template>
    <head>
        <title>Bereken je zonnepanelen prijs in 30 seconden</title>
    </head>

    <CalculatorLayout>
        <div class="bg-[#F7F9FC] border-b border-[#EBF1F9]">
            <div class="mx-auto pt-6 pb-6 w-[100%] sm:w-[90%] min-w-[290px] max-w-[1170px] flex items-center justify-between grid grid-cols-1 gap-6">

                <div class="pl-20 md:pl-48 lg:pl-16 lg:pr-16 lg:flex lg:flex-row lg:justify-between lg:space-x-1 flex-col space-y-1">
                    <div class="flex items-center space-x-1">
                        <img src="/img/icons/icon_blue_check_in_circle.svg" class="lg:w-6 w-5 lg:h-6 h-5" alt="Blue check icon" />
                        <p class="pl-2 xl:text-lg text-m text-[#2C2C4F]">{{ $t('offer_v1.Quote in') }} <strong>{{ $t('offer_v1.30 seconds') }}</strong></p>
                    </div>
                    <div class="flex items-center space-x-1">
                        <img src="/img/icons/icon_blue_check_in_circle.svg" class="lg:w-6 w-5 lg:h-6 h-5" alt="Blue check icon" />
                        <p class="pl-2 xl:text-lg text-m text-[#2C2C4F]"><strong>{{ $t('offer_v1.Free') }}</strong> {{ $t('offer_v1.technical inspection') }}</p>
                    </div>
                    <div class="flex items-center space-x-1">
                        <img src="/img/icons/icon_blue_check_in_circle.svg" class="lg:w-6 w-5 lg:h-6 h-5" alt="Blue check icon" />
                        <p class="pl-2 xl:text-lg text-m text-[#2C2C4F]"><strong>{{ $t('offer_v1.Installation') }}</strong> {{ $t('offer_v1.within 2-4 weeks') }}</p>
                    </div>
                </div>

                <div class="px-3 md:px-0">
                    <!-- Progress Bar Container -->
                    <div class="w-full bg-[#E3EAF4] h-1.5 rounded-full">
                        <!-- Filled Progress with Transition -->
                        <div :class="progressBarClass" :style="progressStyle"></div>
                    </div>
                    <div class="text-sm mt-1">{{ $t('offer_v1.Step') }} {{ step }}/{{ totalSteps }}</div>
                </div>

                <div v-for="question in questions" :key="question.id" class="mb-1 px-3 md:px-0">
                    <div v-if="question.type === 'radio'">
                        <div class="flex justify-center pb-[45px]">
                            <h2 class="text-[28px] md:text-[38px] font-bold">{{ question.label }}</h2>
                        </div>
                        <label v-for="option in question.options" :key="option.value" :for="`option` + question.id + option.value"
                               class="bg-white block p-6 mb-3 max-w-md mx-auto rounded-[12px] flex items-center space-x-4 border border-[#E3EAF4] cursor-pointer"
                               :class="{'border-green': option.value === answers[question.key]}"
                        >
                            <div class="flex items-center">
                                <input :id="`option` + question.id + option.value" v-model="answers[question.key]"
                                       :value="option.value" type="radio" name="roof" value="tile"
                                       v-on:change="disableErrorMessage" class="rounded-full h-[20px] w-[20px]"
                                >

                                <span class="ml-2 block text-[16px] text-gray-700">
                                    {{ option.label }}
                                </span>
                            </div>
                        </label>

                        <div v-if="errorMessage" class="p-6 mb-3 max-w-md mx-auto rounded-[12px] bg-red-300 flex items-start">
                            <img src="/img/icons/icon_info.svg" class="w-[22px] h-[21px] mt-[2px]" alt="Error icon" />
                            <div class="flex-grow text-xs text-[#005893] ml-4">
                                {{ errorMessage }}
                            </div>
                        </div>

                        <div v-if="question.info" class="p-6 mb-3 max-w-md mx-auto rounded-[12px] bg-[#C5E7FF] flex items-start">
                            <img src="/img/icons/icon_info.svg" class="w-[22px] h-[21px] mt-[2px]" alt="Info icon" />
                            <div class="flex-grow text-xs text-[#005893] ml-4">
                                {{ question.info }}
                            </div>
                        </div>

                    </div>
                </div>

                <div class="flex justify-center">
                    <div class="px-2 md:px-0 min-w-[400px] md:min-w-[440px] flex justify-between">
                        <button v-if="step > 1" @click="previousStep" class="font-weight-normal text-[16px] text-[#959BB5]">
                            <span class="mr-1 text-[18px]">&lt;</span>{{ $t('offer_v1.Back') }}
                        </button>
                        <div v-else>
                            &nbsp;
                        </div>
                        <button @click="nextStep" :disabled="!enableNextButton" class="disabled:bg-gray-300 bg-blue-500 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-md text-[18px]">
                            {{ step === totalSteps-1 ? $t('offer_v1.Results') : $t('offer_v1.Next step') }} <span style="vertical-align: -4px;" class="ml-3 text-2xl font-light">&gt;</span>
                        </button>
                    </div>
                </div>

            </div>
        </div>

        <SolarOfferInfoBlock />
    </CalculatorLayout>
</template>

<script>
import SolarOfferInfoBlock from "@/Components/OfferCalculator/SolarOfferInfoBlock";
import CalculatorLayout from "@/Layouts/CalculatorLayout";
import { Inertia } from '@inertiajs/inertia'
import "vanilla-cookieconsent/dist/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";
import CookieConsentVue from "@/plugins/CookieConsentVue";

export default {
    props: {
        locale: {
            type: String,
            default: 'pl',
        },
    },
    components: {
        CalculatorLayout,
        SolarOfferInfoBlock,
    },
    computed: {
        progressStyle() {
            if (this.step === 0) {
                return { width: '0%' };
            }
            return { width: (this.step / this.totalSteps * 100) + '%' };
        },
        progressBarClass() {
            return 'bg-[#108FE5] h-1.5 rounded-full transition-all duration-500 ease-in-out';
        },
        enableNextButton() {
            if (this.questions.length < 1) {
                return false;
            }

            let enable = true;

            this.questions.forEach((question) => {
                if (question.required && !this.answers[question.key]) {
                    enable = false;
                    return false;
                }
            });

            return enable;
        },
    },
    mounted() {
        CookieConsent.run(CookieConsentVue.options);

        axios.get(
            this.route("api.calculator-new-solar"),
            {

            }
        ).then((response) => {
            if (response.data.calculator_id) {
                this.calculatorId = response.data.calculator_id;
                this.questions = response.data.questions;
                this.step = response.data.step;
                this.totalSteps = response.data.total_steps + 1;
                this.step_title = response.data.step_title;

                this.questions.forEach((question) => {
                    this.answers[question.key] = question.default_value;
                });
            } else {
                console.error('Not initialized');
            }
        });
    },
    data() {
        return {
            calculatorId: null,
            questions: [],
            annualConsumption: 6000,
            step: 0,
            totalSteps: 0,
            step_title: '',
            answers: {},
            errorMessage: null,
        };
    },
    methods: {
        disableErrorMessage() {
            this.errorMessage = null;
        },
        validateAnswers() {
            let isValid = true;

            this.questions.forEach((question) => {
                if (question.required && !this.answers[question.key]) {
                    // this.errorMessage = question.error_message;
                    isValid = false;
                }
            });

            return isValid;
        },
        nextStep() {
            if (!this.validateAnswers()) {
                return;
            }

            axios.post(
                this.route("api.calculator-next-step"),
                {
                    'calculator_id': this.calculatorId,
                    'answers': this.answers,
                    'step': this.step,
                }
            ).then((response) => {
                if (response.data.calculator_id || response.data.step === 0) {
                    if (response.data.step === 0 || this.questions.length === 0) {
                        Inertia.visit(this.route('offer-calculator.results', {'calculatorId': this.calculatorId}), {
                            method: 'get',
                        })
                    } else {
                        this.calculatorId = response.data.calculator_id;
                        this.questions = response.data.questions;
                        this.step = response.data.step;
                        this.totalSteps = response.data.total_steps + 1;
                        this.step_title = response.data.step_title;

                        this.questions.forEach((question) => {
                            this.answers[question.key] = question.default_value;
                        });
                    }
                } else {
                    console.error('Not initialized');
                }
            });
        },
        previousStep() {
            if (this.step < 2) {
                return;
            }
            axios.post(
                this.route("api.calculator-previous-step"),
                {
                    'calculator_id': this.calculatorId,
                    'answers': this.answers,
                    'step': this.step,
                }
            ).then((response) => {
                if (response.data.calculator_id) {
                    this.calculatorId = response.data.calculator_id;
                    this.questions = response.data.questions;
                    this.step = response.data.step;
                    this.totalSteps = response.data.total_steps + 1;
                    this.step_title = response.data.step_title;

                    this.questions.forEach((question) => {
                        this.answers[question.key] = question.default_value;
                    });

                    console.log(this.questions);
                } else {
                    console.error('Not initialized');
                }
            });
        },
    },
};
</script>

<style>
#cc-main button:first-of-type {
    --cc-btn-primary-bg: #3b76ee;
    --cc-btn-primary-border-color: #3b76ee;
    --cc-btn-primary-hover-bg: #2343cc;
    --cc-btn-primary-hover-border-color: #2343cc;
}
</style>
